import { QueryClient, QueryKey } from '@tanstack/react-query'
import {
  postFeedQueryKey,
  postPinnedQueryKey,
  postRowEntityKey,
  postScheduledQueryKey,
} from '../../api/posts'
import { groupQueries } from '../../api/groups'
import { contactQueries } from '../../api/society-contacts'
import { faqQueries } from '../../api/faqs'
import { houseRuleQueries } from '../../api/house-rules'
import { resourceQueries } from '../../api/resources'
import { widgetQueries } from '../../api/society-widgets'
import { contractQueries } from '../../api/contracts'
import { facilityQueries } from '../../api/facilities'
import { documentQueries } from '../../api/documents'
import { localOfferQueries } from '../../api/local-offers'
import { websiteQueries } from '../../api/society-websites'
import { societyGuidesQueries } from '../../api/society-guides'
import { boardRoomContactQueries } from '../../api/society-boardroom-contacts'
import {
  ContentUpdatedKind,
  ContentUpdatedPayload,
} from '../../generated/api/live-events'
import { PostRow } from '../../generated/api/post-row'
import { refreshRowsHolding } from './postRowCache'

type ContentRule = (
  client: QueryClient,
  payload: ContentUpdatedPayload
) => Promise<unknown>

interface ContentKeys {
  list: (societyId: string) => QueryKey
  detail?: (id: string) => QueryKey
  cards?: (row: PostRow) => { id: string }[]
}

const contentRule =
  ({ list, detail, cards }: ContentKeys): ContentRule =>
  (client, { societyId, id, action }) =>
    Promise.all([
      client.invalidateQueries({ queryKey: list(societyId) }),
      action !== 'created' &&
        detail &&
        client.invalidateQueries({ queryKey: detail(id) }),
      action === 'edited' &&
        cards &&
        refreshRowsHolding(client, (row) =>
          cards(row).some((card) => card.id === id)
        ),
    ])

const facilityRule = contentRule({
  list: (societyId) =>
    facilityQueries.getFacilitiesPerSociety(societyId).queryKey,
  detail: (id) => facilityQueries.getFacility(id).queryKey,
  cards: ({ facilities }) => facilities,
})

const contentRules: Record<ContentUpdatedKind, ContentRule> = {
  group: (client, { societyId, id, action }) => {
    const listRefetch = client.invalidateQueries({
      queryKey: groupQueries.getGroupsPerSociety(societyId).queryKey,
    })
    if (action === 'created') {
      return listRefetch
    }
    // Rows carry no group ids; a moved group's old members get the new society.
    return Promise.all([
      listRefetch,
      client.invalidateQueries({
        queryKey: groupQueries.getGroup(id).queryKey,
      }),
      ...[
        postFeedQueryKey,
        postPinnedQueryKey,
        postScheduledQueryKey,
        postRowEntityKey,
      ].map((queryKey) => client.invalidateQueries({ queryKey })),
    ])
  },
  faq: contentRule({
    list: (societyId) => faqQueries.getFaqsPerSociety(societyId).queryKey,
  }),
  'house-rule': contentRule({
    list: (societyId) =>
      houseRuleQueries.getHouseRulesPerSociety(societyId).queryKey,
  }),
  'society-contact': contentRule({
    list: (societyId) =>
      contactQueries.getContactsPerSociety(societyId).queryKey,
    detail: (id) => contactQueries.getContact(id).queryKey,
    cards: ({ contacts }) =>
      contacts.filter((contact) => contact.kind === 'society'),
  }),
  'boardroom-contact': contentRule({
    list: (societyId) =>
      boardRoomContactQueries.getContactsPerSociety(societyId).queryKey,
    detail: (id) => boardRoomContactQueries.getContact(id).queryKey,
    cards: ({ contacts }) =>
      contacts.filter((contact) => contact.kind === 'boardroom'),
  }),
  resource: contentRule({
    list: (societyId) =>
      resourceQueries.getResourcesPerSociety(societyId).queryKey,
    detail: (id) => resourceQueries.getResource(id).queryKey,
    cards: ({ resources }) => resources,
  }),
  widget: contentRule({
    list: (societyId) => widgetQueries.getWidgetsPerSociety(societyId).queryKey,
    detail: (id) => widgetQueries.getWidget(id).queryKey,
  }),
  contract: contentRule({
    list: (societyId) =>
      contractQueries.getContractsPerSociety(societyId).queryKey,
    detail: (id) => contractQueries.getContract(id).queryKey,
  }),
  facility: (client, payload) => {
    const { societyId, id, action } = payload
    if (action !== 'deleted') {
      return facilityRule(client, payload)
    }
    client.removeQueries({ queryKey: facilityQueries.getFacility(id).queryKey })
    client.removeQueries({
      queryKey: [{ scope: 'facility', entity: 'facility', facilityId: id }],
    })
    return client.invalidateQueries({
      queryKey: facilityQueries.getFacilitiesPerSociety(societyId).queryKey,
    })
  },
  document: contentRule({
    list: (societyId) => documentQueries.getPerSocieties(societyId).queryKey,
    cards: ({ documents }) => documents,
  }),
  'local-offer': contentRule({
    list: (societyId) =>
      localOfferQueries.getLocalOffersPerSocieties(societyId).queryKey,
    detail: (id) => localOfferQueries.getLocalOffer(id).queryKey,
    cards: ({ offers }) => offers,
  }),
  website: contentRule({
    list: (societyId) =>
      websiteQueries.getWebsitePerSociety(societyId).queryKey,
  }),
  guide: (client, { societyId, id }) =>
    client.invalidateQueries({
      queryKey: (id === 'getToKnow'
        ? societyGuidesQueries.getToKnow
        : societyGuidesQueries.gettingStarted)(societyId).queryKey,
    }),
}

export const applyContentUpdated = async (
  client: QueryClient,
  payload: ContentUpdatedPayload
): Promise<void> => {
  // A newer api may send a kind this build doesn't know.
  await contentRules[payload.kind]?.(client, payload)
}
